<template>
  <SalesMultiCustomerPageShell
    title="Multicliente"
    subtitle="Un solo negocio, varios clientes: coordina alcance, relación y valor en una misma operación"
    :actions="headerActions"
    @action="onHeaderAction"
  >
    <template #metrics>
      <section class="metrics-grid">
        <SalesMultiCustomerKpiCard
          v-for="metric in kpiCards"
          :key="metric.title"
          :title="metric.title"
          :value="metric.value"
          :value-route="metric.valueRoute"
          :value-link-class="metric.valueLinkClass"
          :value-scale="metric.valueScale"
          :truncate-value="metric.truncateValue"
          :value-meta="metric.valueMeta"
          :extra-values="metric.extraValues"
          :icon="metric.icon"
          :icon-bg="metric.iconBg"
          :icon-color="metric.iconColor"
        />
      </section>
    </template>

    <template #toolbar>
      <SharedSalesToolbar
        mode="multi-customer"
        @search-change="handleToolbarSearch"
        @filters-change="handleToolbarFilters"
      />
    </template>

    <template #tabs>
      <StatusTabs
        :tabs="statusTabs"
        :active-tab="activeStatusTab"
        @select="selectStatusTab"
      />
    </template>

    <template #table>
      <SharedSalesTable
        :rows="paginatedRows"
        :loading="tableLoading"
        empty-text="Sin negocios disponibles para los filtros seleccionados."
        :pagination="salesPagination"
        :show-customer-column="false"
        :sort-field="sortField"
        :sort-direction="sortDirection"
        @change-sale-status="handleChangeSaleStatus"
        @edit-sale="handleEditSale"
        @delete-sale="handleDeleteSale"
        @page-change="handlePageChange"
        @sort-change="handleSortChange"
      />
    </template>

    <template #modal>
      <div>
        <SaleModal
          :show="showCreateSaleMultiCustomerModal"
          :mode="saleModalMode"
          :initial-data="saleModalInitialData"
          customer-mode="multi"
          :submitting="createSaleSubmitting"
          :advertising-enabled="isAdvertisingModuleActive"
          @close="closeCreateSaleMultiCustomerModal"
          @delete="handleDeleteSaleFromModal"
          @submit="handleCreateSale"
        />

        <ConfirmActionModal
          :show="isDeleteConfirmModalOpen"
          title="Eliminar negocio multicliente"
          subtitle="Esta acción eliminará el negocio multicliente seleccionado."
          :message="deleteConfirmMessage"
          confirm-text="Eliminar"
          @cancel="closeDeleteConfirmModal"
          @confirm="confirmDeleteSale"
        />

        <SaleStatusModal
          :show="isStatusModalOpen"
          :sale-name="resolveSelectedSaleName()"
          :current-status-label="resolveSelectedSaleStatusLabel()"
          :statuses="statusOptions"
          :selected-status-id="resolveSelectedSaleStatusId()"
          :submitting="statusModalSubmitting"
          @close="closeStatusModal"
          @submit="confirmUpdateSaleStatus"
        />
      </div>
    </template>
  </SalesMultiCustomerPageShell>
</template>

<script>
import SalesMultiCustomerKpiCard from '@/components/salesMultiCustomer/SalesMultiCustomerKpiCard.vue';
import SalesMultiCustomerPageShell from '@/components/salesMultiCustomer/SalesMultiCustomerPageShell.vue';
import SharedSalesToolbar from '@/components/sales/shared/SalesToolbar.vue';
import StatusTabs from '@/components/common/StatusTabs.vue';
import SharedSalesTable from '@/components/sales/shared/SalesTable.vue';
import SaleStatusModal from '@/components/sales/shared/SaleStatusModal.vue';
import SaleModal from '@/components/sales/SaleModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import salesDetailService from '@/services/sales';
import salesService from '@/services/salesMultiCustomer';
import { formatCurrency } from '@/utils/numberFormat';
import {
  extractApiErrorMessage,
  extractApiSuccessMessage,
  extractCollection,
  extractPayload,
} from '@/utils/dataNormalization';
import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import { CAPABILITIES_UPDATED_EVENT } from '@/utils/capabilities';
import {
  createDefaultSalesMultiCustomerToolbarFilters,
  normalizeSalesMultiCustomerToolbarFilters,
  buildSalesMultiCustomerListParams,
} from '@/composables/salesMultiCustomer/useSalesMultiCustomerFilters';
import {
  buildSalesMultiCustomerStatusTabs,
  filterSalesMultiCustomerRowsByStatus,
} from '@/composables/salesMultiCustomer/useSalesMultiCustomerStatusTabs';
import {
  createInitialSalesMultiCustomerMetricsValues,
  mapSalesMultiCustomerMetricsPayload,
  buildSalesMultiCustomerMetricsFromRows,
  buildSalesMultiCustomerKpiCards,
} from '@/composables/salesMultiCustomer/useSalesMultiCustomerMetrics';
import {
  buildCreateSaleMultiCustomerRequestData,
  buildUpdateSaleMultiCustomerRequestData,
} from '@/composables/salesMultiCustomer/useSalesMultiCustomerCreateModal';
import { fetchSalesMultiCustomerRows } from '@/composables/salesMultiCustomer/useSalesMultiCustomerList';
import {
  resolveTotalPages,
  ensureValidPage,
  sliceRowsByPage,
  buildPaginationPayload,
} from '@/composables/shared/usePagination';
import { resolveSortChange, sortRows } from '@/composables/shared/useSorting';

export default {
  name: 'SalesMultiCustomerPage',
  components: {
    SalesMultiCustomerKpiCard,
    SalesMultiCustomerPageShell,
    SharedSalesToolbar,
    StatusTabs,
    SharedSalesTable,
    SaleStatusModal,
    SaleModal,
    ConfirmActionModal,
  },
  data() {
    return {
      headerActionsBase: [
        {
          key: 'new-sale',
          label: 'Nuevo',
          icon: 'fa-plus',
          variant: 'primary',
        },
      ],
      headerActions: [],
      tableLoading: false,
      salesRows: [],
      activeStatusTab: 'all',
      toolbarSearch: '',
      toolbarFilters: createDefaultSalesMultiCustomerToolbarFilters(),
      searchDebounceMs: 1000,
      searchDebounceTimer: null,
      listRequestToken: 0,
      currentPage: 1,
      pageSize: 30,
      sortField: 'sale_date',
      sortDirection: 'desc',
      showCreateSaleMultiCustomerModal: false,
      saleModalMode: 'create',
      saleModalInitialData: null,
      editingSaleId: 0,
      createSaleSubmitting: false,
      isStatusModalOpen: false,
      statusModalSubmitting: false,
      selectedSaleForStatus: null,
      statusOptions: [],
      isDeleteConfirmModalOpen: false,
      deletingSale: null,
      deletingSaleSource: 'table',
      lastFetchParamsKey: '',
      hasLoadedListOnce: false,
      metricsValues: createInitialSalesMultiCustomerMetricsValues(),
    };
  },
  computed: {
    normalizedRows() {
      return Array.isArray(this.salesRows) ? this.salesRows : [];
    },
    statusTabs() {
      return buildSalesMultiCustomerStatusTabs(this.normalizedRows);
    },
    filteredRows() {
      return filterSalesMultiCustomerRowsByStatus(this.normalizedRows, this.activeStatusTab);
    },
    sortedRows() {
      return sortRows(this.filteredRows, {
        field: this.sortField,
        direction: this.sortDirection,
      }, {
        sale_date: (row) => new Date(row?.sale_date || row?.date || 0).getTime(),
        customer: (row) => String(row?.company_name || row?.customer_name || row?.cliente || '').trim().toLowerCase(),
        sale_name: (row) => String(row?.sale_name || row?.project || row?.name || row?.code || '').trim().toLowerCase(),
        service: (row) => String(row?.service_name || row?.service || row?.service_type || '').trim().toLowerCase(),
        subtotal: (row) => Number(row?.subtotal || 0),
        balance: (row) => Number(row?.balance ?? row?.saldo ?? 0),
      });
    },
    totalPages() {
      return resolveTotalPages(this.sortedRows.length, this.pageSize);
    },
    paginatedRows() {
      return sliceRowsByPage(this.sortedRows, this.currentPage, this.pageSize);
    },
    salesPagination() {
      return buildPaginationPayload({
        currentPage: this.currentPage,
        pageSize: this.pageSize,
        totalItems: this.sortedRows.length,
      });
    },
    kpiCards() {
      return buildSalesMultiCustomerKpiCards(this.metricsValues, formatCurrency);
    },
    deleteConfirmMessage() {
      const saleName = String(this.deletingSale?.sale_name || this.deletingSale?.name || '').trim();
      const saleLabel = saleName || 'Sin nombre';

      return `Negocio multicliente: ${saleLabel}\n¿Deseas continuar con la eliminación?`;
    },
    isAdvertisingModuleActive() {
      const can = typeof this.$can === 'function' ? this.$can : null;
      if (!can) {
        return true;
      }

      return can('sales.advertising');
    },
  },
  mounted() {
    this.refreshHeaderActions();
    window.addEventListener(CAPABILITIES_UPDATED_EVENT, this.refreshHeaderActions);
  },
  beforeUnmount() {
    window.removeEventListener(CAPABILITIES_UPDATED_EVENT, this.refreshHeaderActions);

    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = null;
    }
  },
  methods: {
    refreshHeaderActions() {
      const can = typeof this.$can === 'function' ? this.$can : () => false;
      this.headerActions = this.headerActionsBase.filter((action) => {
        if (action?.key === 'new-sale') {
          return can('sales.button_new') || can('sales.create');
        }


        return true;
      });
    },
    onHeaderAction(action) {
      const actionKey = String(action?.key || '').trim();

      if (actionKey === 'new-sale') {
        this.openCreateSaleMultiCustomerModal();
      }

    },
    openCreateSaleMultiCustomerModal() {
      this.saleModalMode = 'create';
      this.saleModalInitialData = null;
      this.editingSaleId = 0;
      this.showCreateSaleMultiCustomerModal = true;
    },
    closeCreateSaleMultiCustomerModal(force = false) {
      if (!force && this.createSaleSubmitting) {
        return;
      }

      this.showCreateSaleMultiCustomerModal = false;

      if (force) {
        this.saleModalMode = 'create';
        this.saleModalInitialData = null;
        this.editingSaleId = 0;
      }
    },
    async handleCreateSale(payload) {
      this.createSaleSubmitting = true;

      try {
        const isEditMode = this.saleModalMode === 'edit' && Number(this.editingSaleId || 0) > 0;
        const requestData = isEditMode
          ? buildUpdateSaleMultiCustomerRequestData(payload)
          : buildCreateSaleMultiCustomerRequestData(payload);

        const response = isEditMode
          ? await salesService.update(Number(this.editingSaleId), requestData)
          : await salesService.store(requestData);

        const apiErrorMessage = extractApiErrorMessage(
          response,
          isEditMode
            ? 'No fue posible actualizar el negocio multicliente.'
            : 'No fue posible registrar el negocio.',
        );

        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        const createdSale = extractPayload(response);
        const createdSaleId = Number(createdSale?.id || createdSale?.sale_id || 0);

        this.$toast?.success(
          extractApiSuccessMessage(
            response,
            isEditMode
              ? 'El negocio multicliente ha sido actualizado correctamente.'
              : 'El negocio ha sido registrado correctamente.',
          )
        );
        this.closeCreateSaleMultiCustomerModal(true);

        if (!isEditMode && createdSaleId > 0) {
          this.$router.push({ name: 'saleDetail', params: { saleId: createdSaleId } }).catch(() => {});
          return;
        }

        if (!isEditMode) {
          this.activeStatusTab = 'all';
        }

        await this.fetchSales(true);
      } catch (error) {
        this.$toast?.error(
          error?.response?.data?.message
            || (this.saleModalMode === 'edit'
              ? 'No fue posible actualizar el negocio multicliente.'
              : 'No fue posible registrar el negocio.')
        );
      } finally {
        this.createSaleSubmitting = false;
      }
    },
    ensureValidPage() {
      this.currentPage = ensureValidPage(this.currentPage, this.totalPages);
    },
    scheduleSearchFetch() {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      this.searchDebounceTimer = setTimeout(() => {
        this.searchDebounceTimer = null;
        this.fetchSales();
      }, this.searchDebounceMs);
    },
    buildListParams() {
      return buildSalesMultiCustomerListParams({
        toolbarFilters: this.toolbarFilters,
        toolbarSearch: this.toolbarSearch,
      });
    },
    buildFetchParamsKey() {
      return JSON.stringify(this.buildListParams());
    },
    async fetchSales(force = false) {
      const paramsKey = this.buildFetchParamsKey();

      if (!force && this.tableLoading && this.lastFetchParamsKey === paramsKey) {
        return;
      }

      if (!force && this.hasLoadedListOnce && this.lastFetchParamsKey === paramsKey) {
        return;
      }

      this.lastFetchParamsKey = paramsKey;
      const requestToken = ++this.listRequestToken;
      this.tableLoading = true;

      try {
        const rows = await fetchSalesMultiCustomerRows(salesService, this.buildListParams());

        if (requestToken === this.listRequestToken) {
          this.salesRows = rows;
          this.ensureValidPage();
        }
      } catch (error) {
        if (requestToken === this.listRequestToken) {
          this.salesRows = [];
        }
      } finally {
        if (requestToken === this.listRequestToken) {
          this.tableLoading = false;
          this.hasLoadedListOnce = true;
          await this.fetchMetrics();
        }
      }
    },
    async fetchMetrics() {
      try {
        const response = await salesService.metrics(this.buildListParams());
        const payload = extractPayload(response);

        this.metricsValues = mapSalesMultiCustomerMetricsPayload(payload);
      } catch (error) {
        this.syncMetricsFromRows();
      }
    },
    syncMetricsFromRows() {
      this.metricsValues = buildSalesMultiCustomerMetricsFromRows(this.normalizedRows);
    },
    handleToolbarSearch(value) {
      this.toolbarSearch = String(value || '');
      this.currentPage = 1;
      this.scheduleSearchFetch();
    },
    handleToolbarFilters(filters) {
      const normalized = normalizeSalesMultiCustomerToolbarFilters(filters);
      this.toolbarFilters = normalized;
      this.currentPage = 1;

      if (Object.prototype.hasOwnProperty.call(normalized, 'search')) {
        this.toolbarSearch = String(normalized.search || '');
      }

      this.fetchSales();
    },
    selectStatusTab(statusKey) {
      this.activeStatusTab = statusKey;
      this.currentPage = 1;
      this.ensureValidPage();
    },
    handlePageChange(nextPage) {
      this.currentPage = Number(nextPage || 1);
      this.ensureValidPage();
    },
    handleSortChange(payload = {}) {
      const nextSort = resolveSortChange(
        {
          field: this.sortField,
          direction: this.sortDirection,
        },
        payload,
        ['sale_date', 'customer', 'sale_name', 'service', 'subtotal', 'balance'],
      );

      if (nextSort.field === this.sortField && nextSort.direction === this.sortDirection) {
        return;
      }

      this.sortField = nextSort.field;
      this.sortDirection = nextSort.direction;
      this.currentPage = 1;
      this.ensureValidPage();
    },
    async handleChangeSaleStatus(row) {
      const saleId = Number(row?.id || 0);
      if (!saleId) {
        return;
      }

      this.selectedSaleForStatus = row;
      this.isStatusModalOpen = true;
      await this.loadStatusOptions();
    },
    closeStatusModal(force = false) {
      if (!force && this.statusModalSubmitting) {
        return;
      }

      this.isStatusModalOpen = false;
      this.selectedSaleForStatus = null;
    },
    resolveSelectedSaleName() {
      return String(this.selectedSaleForStatus?.sale_name || this.selectedSaleForStatus?.name || '').trim();
    },
    resolveSelectedSaleStatusLabel() {
      return String(this.selectedSaleForStatus?.status_name || this.selectedSaleForStatus?.status || '').trim();
    },
    resolveSelectedSaleStatusId() {
      return Number(this.selectedSaleForStatus?.status_sale_id || 0);
    },
    async loadStatusOptions() {
      if (this.statusOptions.length > 0) {
        return;
      }

      try {
        const response = await salesService.statuses({}, { skipLoading: true });
        const collection = extractCollection(response);
        this.statusOptions = normalizeCatalogOptions(collection, { useCodeInLabel: false });
      } catch (error) {
        this.statusOptions = [];
        this.$toast?.error(error?.response?.data?.message || 'No fue posible cargar los estados de negocio.');
      }
    },
    async confirmUpdateSaleStatus(statusSaleId) {
      const saleId = Number(this.selectedSaleForStatus?.id || 0);
      const nextStatusId = Number(statusSaleId || 0);
      if (!saleId || !nextStatusId) {
        return;
      }

      this.statusModalSubmitting = true;

      try {
        const response = await salesService.updateStatus(saleId, {
          status_sale_id: nextStatusId,
        });

        const apiErrorMessage = extractApiErrorMessage(response, 'No fue posible actualizar el estado del negocio multicliente.');
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.$toast?.success(extractApiSuccessMessage(response, 'El estado del negocio multicliente ha sido actualizado correctamente.'));
        this.closeStatusModal(true);
        await this.fetchSales(true);
      } catch (error) {
        this.$toast?.error(error?.response?.data?.message || 'No fue posible actualizar el estado del negocio multicliente.');
      } finally {
        this.statusModalSubmitting = false;
      }
    },
    async handleEditSale(row) {
      const saleId = Number(row?.id || 0);
      if (!saleId) {
        return;
      }

      try {
        const response = await salesDetailService.show(saleId, {}, { skipLoading: true });
        const detailPayload = extractPayload(response);

        if (!detailPayload || typeof detailPayload !== 'object') {
          this.$toast?.error('No fue posible cargar el detalle del negocio para edición.');
          return;
        }

        this.saleModalMode = 'edit';
        this.editingSaleId = saleId;
        this.saleModalInitialData = detailPayload;
        this.showCreateSaleMultiCustomerModal = true;
      } catch (error) {
        this.$toast?.error(error?.response?.data?.message || 'No fue posible cargar el negocio para edición.');
      }
    },
    handleDeleteSale(row) {
      const saleId = Number(row?.id || 0);
      if (!saleId) {
        return;
      }

      this.deletingSale = row;
      this.deletingSaleSource = 'table';
      this.isDeleteConfirmModalOpen = true;
    },
    handleDeleteSaleFromModal(sale) {
      const saleId = Number(sale?.id || this.editingSaleId || 0);
      if (!saleId) {
        return;
      }

      this.deletingSale = {
        ...(sale && typeof sale === 'object' ? sale : {}),
        id: saleId,
        sale_name: String(
          sale?.sale_name
          || sale?.name
          || this.saleModalInitialData?.sale_name
          || this.saleModalInitialData?.name
          || ''
        ).trim(),
      };
      this.deletingSaleSource = 'modal';
      this.isDeleteConfirmModalOpen = true;
    },
    closeDeleteConfirmModal() {
      this.isDeleteConfirmModalOpen = false;
      this.deletingSale = null;
      this.deletingSaleSource = 'table';
    },
    async confirmDeleteSale() {
      const saleId = Number(this.deletingSale?.id || 0);
      if (!saleId) {
        this.closeDeleteConfirmModal();
        return;
      }

      try {
        const response = await salesService.delete(saleId);
        const apiErrorMessage = extractApiErrorMessage(response, 'No fue posible eliminar el negocio multicliente.');
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.$toast?.success(extractApiSuccessMessage(response, 'El negocio multicliente ha sido eliminado correctamente.'));
        if (this.deletingSaleSource === 'modal') {
          this.closeCreateSaleMultiCustomerModal(true);
        }
        this.closeDeleteConfirmModal();
        await this.fetchSales(true);
      } catch (error) {
        this.$toast?.error(error?.response?.data?.message || 'No fue posible eliminar el negocio multicliente.');
      }
    },
  },
};
</script>

<style scoped>
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
}
</style>
